import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  ViewChild,
} from '@angular/core';
import {
  NavigationEnd,
  NavigationError,
  NavigationStart,
  Router,
  RouterEvent,
} from '@angular/router';
import {AuthService} from '@drl/core/auth/auth.service';
import {ComponentBase} from '@drl/core/component-base';
import {UserSessionStoreService as StoreService} from '@drl/core/store/user-session-store.service';
import {NbMenuItem, NbMenuService, NbSidebarService} from '@nebular/theme';
import {isEqual, keys, cloneDeep} from 'lodash-es';
import {NgxPermissionsService} from 'ngx-permissions';
import {distinctUntilChanged, filter} from 'rxjs/operators';

import {menuItem} from './pages-menu';
import {patientMenuItem} from './patient-pages-menu';
import {IpsTranslateService} from '@drl/core/localization/ips-translate.service';
import {AppoinmentService} from './care-central/appoinment.service';
import {TranslateService} from '@ngx-translate/core';
import {SystemStoreFacadeService} from '@drl/core/store/system-store-facade.service';
import {
  FeatureConfigKeys,
  SubFeatureConfigKeys,
} from '@drl/core/enums/feature-config-keys.enum';
import {FeatureModel} from '@drl/core/auth/models/feature.model';
import {
  LenangioLogoLoaderService,
  LoaderState,
} from '../lenangio-logo-loader/lenangio-logo-loader.service';
import {MenuService} from './menu.service';

const patientsPath = '/main/patients';

@Component({
  selector: 'app-main',
  templateUrl: './main.component.html',
  styleUrls: ['./main.component.scss'],
})
export class MainComponent extends ComponentBase {
  @ViewChild('scrollBottom') private readonly scrollBottom: ElementRef;

  menuItems: NbMenuItem[] = cloneDeep(menuItem(this.translateSvc.translate));
  patientMenuItems: NbMenuItem[] = cloneDeep(
    patientMenuItem(this.translateSvc.translate),
  );

  filteredMenuItems: NbMenuItem[] = [];
  patientMenuSet = false;
  isOnlineAccess: boolean;
  loading: boolean;
  leftMenuLoading: boolean;
  isVCEnabled = false;
  copyRightLbl: string;
  featureConfigs: FeatureModel[];
  lengioLoaderState: LoaderState;
  currentUrl = '';

  constructor(
    private readonly permissionsService: NgxPermissionsService,
    protected readonly route: Router,
    private readonly store: StoreService,
    private readonly authService: AuthService,
    private readonly menu: NbMenuService,
    private readonly sidebarService: NbSidebarService,
    protected readonly translateSvc: IpsTranslateService,
    public readonly patientService: AppoinmentService,
    public readonly translate: TranslateService,
    private readonly systemStoreFacade: SystemStoreFacadeService,
    private loaderService: LenangioLogoLoaderService,
    private cdr: ChangeDetectorRef,
    private menuService: MenuService,
  ) {
    super(route);

    const ipadStdSize = 1200;
    this.leftMenuLoading = true;

    // Fetching feature configurations and setting up the menu
    this.systemStoreFacade.getFeatureConfigs().subscribe(fConfigs => {
      if (fConfigs?.features) {
        this.featureConfigs = fConfigs.features;
        this.setMenus();
      }
      this.leftMenuLoading = false;
    });

    // Setup patient-specific menu items
    this.setPatientMenu();

    menu.onSubmenuToggle().subscribe(event => {
      if (event.item.title === translate.instant('configurationsLeftMenuLbl')) {
        setTimeout(() => {
          if (this.scrollBottom?.nativeElement) {
            this.scrollBottom.nativeElement.scrollTop +=
              this.scrollBottom.nativeElement.scrollHeight;
          }
        }, 400);
      }
    });

    menu.onItemClick().subscribe(() => {
      if (window.screen.availWidth <= ipadStdSize) {
        this.sidebarService.compact('menu-sidebar');
      }
    });

    route.events.subscribe(val => {
      if (
        'url' in val &&
        val.url?.includes(`${patientsPath}/`) &&
        !val.url.includes('add-update') &&
        !this.patientMenuSet
      ) {
        const appendUrl = this.getAppendUrl(val.url);
        this.currentUrl = appendUrl;
        this.menuItems = this.getAbsUrl(
          cloneDeep(this.patientMenuItems),
          appendUrl,
        );
        this.patientMenuSet = true;
      } else {
        this.menuItems = cloneDeep(menuItem(this.translateSvc.translate));
        this.patientMenuSet = false;
        this.resetPatientMenu(this.patientMenuItems);
      }
      this.setMenus();
    });
  }

  setPatientMenu() {
    this.menuService.menuItems$.subscribe(menuItems => {
      if (menuItems?.length) {
        this.patientMenuItems = cloneDeep(menuItems);
        const appendUrl = this.getAppendUrl(this.currentUrl);
        this.currentUrl = appendUrl;
        this.menuItems = this.getAbsUrl(
          cloneDeep(this.patientMenuItems),
          appendUrl,
        );
        this.patientMenuSet = true;
        this.setMenus();
      }
    });
  }

  getAppendUrl(url: string) {
    const result = url.replace(`${patientsPath}/`, '');
    return `${patientsPath}/${
      result.includes('/')
        ? result.substring(0, result.indexOf('/') + 1)
        : result
    }`;
  }

  getAbsUrl(menu: NbMenuItem[], appendUrl: string): NbMenuItem[] {
    return menu.map(item => ({
      ...item,
      link: item.link === patientsPath ? item.link : appendUrl + item.link,
      children: item.children
        ? this.getAbsUrl(item.children, appendUrl)
        : undefined,
    }));
  }

  resetPatientMenu(menu: NbMenuItem[]) {
    menu.forEach(item => {
      if (item.link !== patientsPath) {
        item.link = item.link.replace(`${patientsPath}/`, '').split('/').pop();
      }
      if (item.children) {
        this.resetPatientMenu(item.children);
      }
    });
  }

  ngOnInit() {
    super.ngOnInit();

    this.copyRightLbl = this.translate.instant('Copyright');

    this._subscriptions.push(
      this.loaderService.loader$.subscribe(({loading, message}) => {
        this.lengioLoaderState = {loading, message};
        if (loading) {
          [...this.menuItems, ...this.patientMenuItems].forEach(item => {
            item.data = {
              ...(item.data || {}),
              loader: this.lengioLoaderState,
            };
          });
        }
      }),
    );
  }

  private setMenus() {
    this.permissionsService.permissions$
      .pipe(distinctUntilChanged((a, b) => isEqual(keys(a), keys(b))))
      .subscribe(() => {
        this._setHiddenMenus(this.menuItems);
        this.filteredMenuItems = this.menuItems;
      });

    this._subscriptions.push(
      this.route.events
        .pipe(filter(event => event instanceof RouterEvent))
        .subscribe(event => {
          if (event instanceof NavigationStart) {
            const authToken = this.store.getAccessToken();
            if (!authToken && event.url !== '/login') {
              this.authService.logout();
            }
          }

          if (event instanceof NavigationEnd) {
            this.filteredMenuItems = this.menuItems.map(item => ({
              ...item,
              selected: item.link === this.route.url,
            }));
          }

          if (event instanceof NavigationError) {
            // unchanged
          }
        }),
    );
  }

  private _setHiddenMenus(menuItems: NbMenuItem[]) {
    menuItems.forEach(async menuToHide => {
      await this._setHidden(menuToHide);
      if (menuToHide.children?.length) {
        this._setHiddenMenus(menuToHide.children);
      }
    });
  }

  private _setHidden(mItem: NbMenuItem) {
    if (mItem.data?.permission && mItem.data.permission !== '*') {
      const permissions = Array.isArray(mItem.data.permission)
        ? mItem.data.permission
        : [mItem.data.permission];

      const hasPermission = permissions.some(p =>
        this.permissionsService.getPermission(p),
      );

      mItem.hidden = !hasPermission;
    } else {
      mItem.hidden = false;
    }

    if (mItem.title === this.translate.instant('dietPlansTitle')) {
      mItem.hidden = this._checkForFeatureConfigs(
        mItem,
        FeatureConfigKeys.Diet,
      );
    }
  }

  private _checkForFeatureConfigs(
    mItem: NbMenuItem,
    featureKey: FeatureConfigKeys,
  ) {
    if (featureKey === FeatureConfigKeys.Diet && this.featureConfigs) {
      const dietObj = this.featureConfigs.find(
        f => f.featureKey === FeatureConfigKeys.Diet,
      );
      if (!dietObj?.enabled) return true;

      const dietPlanObj = dietObj.subFeatures.find(
        s => s.subFeatureKey === SubFeatureConfigKeys.DietPlan,
      );
      return !dietPlanObj?.enabled;
    }
    return mItem.hidden;
  }
}
